<script setup>
// NOTICE: KAPTION-INTEGRATION
// embedded-channel.DASHBOARD.3 — Web tab: Channel ID, the website snippet
// (window.rumoSettings.channel, embedded-channel.WEB.4), the setUser call with an
// identity token and the allowed domains (the same inbox field the upstream
// Configuration tab edits).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import {
  embeddedChannelId,
  installationUrl,
  webSetUserSnippet,
  webSnippet,
} from 'dashboard/helper/kaptionEmbedded';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ inbox: { type: Object, required: true } });

const { t } = useI18n();
const store = useStore();

const channelId = computed(() => embeddedChannelId(props.inbox));
const snippet = computed(() =>
  webSnippet({
    channelId: channelId.value,
    baseUrl: installationUrl(props.inbox),
  })
);
const setUserSnippet = webSetUserSnippet();

const allowedDomains = ref(props.inbox.allowed_domains || '');
const isUpdatingDomains = ref(false);
watch(
  () => props.inbox.allowed_domains,
  value => {
    allowedDomains.value = value || '';
  }
);

const updateAllowedDomains = async () => {
  isUpdatingDomains.value = true;
  const sanitized = sanitizeAllowedDomains(allowedDomains.value);
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: { allowed_domains: sanitized },
    });
    allowedDomains.value = sanitized;
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdatingDomains.value = false;
  }
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.CHANNEL_ID.TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.CHANNEL_ID.DESCRIPTION')"
    >
      <woot-code :script="channelId" lang="plaintext" />
    </SettingsSection>
    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.WEB.SNIPPET_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.WEB.SNIPPET_DESC')"
    >
      <woot-code :script="snippet" lang="html" />
    </SettingsSection>
    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.WEB.SET_USER_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.WEB.SET_USER_DESC')"
    >
      <woot-code :script="setUserSnippet" lang="javascript" />
    </SettingsSection>
    <SettingsSection
      :title="t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
      :sub-title="t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.SUBTITLE')"
      :show-border="false"
    >
      <div class="flex flex-col w-full max-w-3xl gap-4">
        <TextArea
          v-model="allowedDomains"
          :placeholder="
            t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
          "
          auto-height
          min-height="8rem"
          class="w-full"
        />
        <div>
          <NextButton
            :label="t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdatingDomains"
            @click="updateAllowedDomains"
          />
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
